import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  OverlayView,
} from '@react-google-maps/api';
import {
  Box,
  IconButton,
  VStack,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import polyline from '@mapbox/polyline';
import RouteForm from '../RouteForm/RouteForm';
import LoadingMap from '../../../Misc/LoadingMap';
import RouteApi from '../../../../api/RouteApi';
import { resolveWeatherIconCode } from '../../../../util/weatherCodeMapper';
import {
  getForecastAtPassingTime,
  formatPassingTime,
} from '../../../../util/passingTimes';
import './MapFrame.scss';
import { MoonIcon, SearchIcon } from '@chakra-ui/icons';
import { FaLink, FaShareAlt, FaChartArea } from 'react-icons/fa';
const MAP_STYLE = {
  LIGHT: [
    {
      featureType: 'poi.business',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
  ],
  DARK: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#242f3e',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#746855',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#242f3e',
        },
      ],
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#263c3f',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#6b9a76',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#38414e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#212a37',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9ca5b3',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#746855',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#1f2835',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#f3d19c',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [
        {
          color: '#2f3948',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#17263c',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#515c6d',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#17263c',
        },
      ],
    },
  ],
};

const center = {
  lat: 20,
  lng: 0,
};

const TravelMode = {
  BICYCLING: 'BICYCLING',
  DRIVING: 'DRIVING',
  TRANSIT: 'TRANSIT',
  WALKING: 'WALKING',
};

function MapFrame(props) {
  const [selectedMarkerPosition, setSelectedMarkerPosition] = useState(null);
  const [map, setMap] = useState(null);
  const [alreadyCalculated, setAlreadyCalculated] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [response, setResponse] = useState({ directions: null, id: null });
  const [errorMessage, setErrorMessage] = useState('');
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [localities, setLocalities] = useState([]);
  const [isLargerThanMobile] = useMediaQuery('(min-width: 768px)');
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [userMarker, setUserMarker] = useState(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const [directionsId, setDirectionsId] = useState(null);
  const [decodedPolyline, setDecodedPolyline] = useState(null);
  const [isContextualMenuVisible, setIsContextualMenuVisible] = useState(false);
  const [actualPolyline, setActualPolyline] = useState(null);
  const [mapStyle, setMapStyle] = useState(MAP_STYLE.DARK);
  const [apiData, setApiData] = useState(null);
  const [originLocation, setOriginLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);

  const {
    setAbsoluteLocalities,
    localitiesAndWeather,
    setLocalitiesAndWeather,
    setIndexToRender,
    setIsLoading,
    indexToRender,
    routeData,
    hasWarningBanner,
    departureTime,
    setDepartureTime,
    onOpenProfile,
    onRouteIdChange,
  } = props;

  const mapOptions = {
    styles: mapStyle,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
  };

  const toast = useToast();
  const { t } = useTranslation();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API,
    libraries: ['places'],
  });

  useEffect(() => {
    if (isLongPress /* && !userMarker.includes(isLongPress) */) {
      setUserMarker(isLongPress);
      setIsContextualMenuVisible(true);
    }
  }, [isLongPress, userMarker]);

  // Surface the active route id (fresh or stored) so the parent can fetch the
  // elevation profile and build the dynamic OG image URL.
  useEffect(() => {
    const id = apiData || routeData?._id || null;
    if (id && onRouteIdChange) onRouteIdChange(id);
  }, [apiData, routeData, onRouteIdChange]);

  // Resolves lat/lng from a locality regardless of storage format:
  // - new format: localityPoint: { lat, lng }
  // - old format: point: [lat, lng]
  const getLocalityLatLng = locality => {
    if (!locality) return { lat: null, lng: null };
    if (locality.localityPoint?.lat !== undefined) {
      return { lat: locality.localityPoint.lat, lng: locality.localityPoint.lng };
    }
    return { lat: locality.point?.[0], lng: locality.point?.[1] };
  };

  useEffect(() => {
    setIsOverlayVisible(false);
    if (indexToRender !== null && localitiesAndWeather) {
      const { lat, lng } = getLocalityLatLng(localitiesAndWeather[indexToRender]);
      setSelectedMarkerPosition({ lat, lon: lng });
      var latLng = new google.maps.LatLng(lat, lng);
      map.panTo(latLng);
    }
  }, [indexToRender, localitiesAndWeather, map]);

  useEffect(() => {
    // Remove the previous polyline when new data is available

    // Render the new polyline if decodedPolyline is available
    if (decodedPolyline && map) {
      map.panTo({ lat: decodedPolyline[0][0], lng: decodedPolyline[0][1] });
      map.setZoom(9);
      const polyline = new window.google.maps.Polyline({
        path: transformToLatLngArray(decodedPolyline),
        map: map, // Your Google Map instance
        strokeColor: '#38A169', // Polyline color
        strokeOpacity: 1.0, // Opacity (0.0 to 1.0)
        strokeWeight: 5, // Line width
        zIndex: 3,
      });

      setActualPolyline(polyline);
    }
  }, [decodedPolyline, map]);

  const [directionsServiceOptions, setDirectionsServiceOptions] = useState({
    destination: '',
    origin: '',
    travelMode: TravelMode.DRIVING,
    waypoints: [],
  });

  const handleDeleteWaypoint = waypointName => {
    // Create a copy of the directionsServiceOptions object
    const updatedDirectionsServiceOptions = { ...directionsServiceOptions };

    // Delete the waypoint from the directionsServiceOptions object
    delete updatedDirectionsServiceOptions.waypoints[`${waypointName}`];

    // Update both state variables
    setDirectionsServiceOptions(updatedDirectionsServiceOptions);
  };

  const directionsCallback = async (result, status) => {
    if (status === 'OK' && result !== null) {
      setOriginLocation(null);
      setDestinationLocation(null);
      const id = Date.now();
      setDirectionsId(id);
      setResponse({ directions: result, id: id });
      let routeMetadata = {};

      const speedValues = {
        [TravelMode.DRIVING]: 90, // 90 km/h for driving
        [TravelMode.BICYCLING]: 15, // 15 km/h for bicycling
        [TravelMode.WALKING]: 5, // 5 km/h for walking
      };

      /**EXTRACT ROUTE INFORMATION FROM THE RESULT OBJECT */
      const polyline_encoded = result.routes[0].overview_polyline;

      const totalDistance = result.routes[0].legs[0].distance.value;
      const duration = result.routes[0].legs[0].duration.value;
      const route = polyline.decode(polyline_encoded);

      setDecodedPolyline(polyline.decode(polyline_encoded));

      const distanceThreshold =
        totalDistance > 200000
          ? totalDistance / 10
          : directionsServiceOptions.travelMode === TravelMode.DRIVING
            ? calculateDistanceThreshold(totalDistance)
            : 10000;

      const initialPoint = route[0];
      initialPoint.timeToArrive = 0;
      map.panTo({ lat: initialPoint[0], lng: initialPoint[1] });
      map.setZoom(9);

      try {
        const storedRoute = await RouteApi.getRoute(
          polyline_encoded,
          directionsServiceOptions.travelMode
        );
        if (storedRoute && storedRoute.status === 'OK') {
          setApiData(storedRoute._id);
          setAbsoluteLocalities(storedRoute.localities);
          return;
        }
      } catch (error) {}

      /**CALCULATE INTERMEDIATE POINTS*/
      let intermediatePoints = [initialPoint];
      let accumulatedDistance = 0;
      for (let i = 1; i < route.length - 1; i++) {
        const prevPoint = intermediatePoints[intermediatePoints.length - 1];
        const currentPoint = route[i];
        const prevLatLng = { lat: prevPoint[0], lng: prevPoint[1] };
        const currentLatLng = { lat: currentPoint[0], lng: currentPoint[1] };
        /* global google */
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
          prevLatLng,
          currentLatLng
        );

        accumulatedDistance += distance / 100;
        const speed = speedValues[directionsServiceOptions.travelMode];

        const timeToArriveInSeconds =
          (accumulatedDistance / 100 / speed) * 3600; // Convert to seconds
        currentPoint.timeToArrive = timeToArriveInSeconds;
        if (
          distance >= distanceThreshold &&
          totalDistance - accumulatedDistance >=
            distanceThreshold + distanceThreshold * 0.2
        ) {
          intermediatePoints.push(currentPoint);
        }
      }
      const finishPoint = route[route.length - 1];
      finishPoint.timeToArrive = duration;
      intermediatePoints.push(finishPoint);

      /**GET LOCALITIES BASED ON INTERMEDIATE POINTS */
      const geocoder = new google.maps.Geocoder();
      const localities = [];
      const localitiesToStore = [];

      let originCountry = null;
      let destinationCountry = null;

      for (let i = 0; i < intermediatePoints.length; i++) {
        const latLng = new google.maps.LatLng(
          intermediatePoints[i][0],
          intermediatePoints[i][1]
        );
        try {
          const results = await geocoder.geocode({ location: latLng });

          if (results.results.length > 0) {
            const country = extractFromGeopoint(results.results[0], 'country');
            if (originCountry === null) {
              originCountry = country;
              destinationCountry = country;
            } else if (originCountry !== country) {
              destinationCountry = country;
            }

            // Extract locality name: try standard locality, then admin_area_level_3,
            // then admin_area_level_1 as fallback, across all geocoding results.
            let localityName = null;
            let province = null;
            let tempLocation = results.results[0].geometry.location;

            for (const geocodeResult of results.results) {
              const candidate =
                extractLocalityFromGeocodingResponse(geocodeResult) ||
                extractLocalityBypassFromGeocodingResponse(geocodeResult);
              if (candidate) {
                localityName = candidate;
                province = extractProvinceFromGeocodingResponse(geocodeResult) || '';
                tempLocation = geocodeResult.geometry.location;
                break;
              }
            }

            if (!localityName) {
              localityName =
                extractFromGeopoint(results.results[0], 'administrative_area_level_1') ||
                country ||
                'Desconocido';
              province = '';
            }

            const extractedLocality = {
              locality: localityName,
              province,
              location: tempLocation,
              country,
              timeToArrive: intermediatePoints[i].timeToArrive,
            };

            localities.push(extractedLocality);
            localitiesToStore.push({
              localityName: extractedLocality.locality,
              localityProvince: extractedLocality.province,
              localityPoint: {
                lat: tempLocation.lat(),
                lng: tempLocation.lng(),
              },
              location: tempLocation,
              country,
              timeToArrive: intermediatePoints[i].timeToArrive,
              point: intermediatePoints[i],
            });
          } else {
            console.error('No results.');
            localities.push(null);
          }
        } catch (error) {
          console.error(`Error geocoding point ${i + 1}:`, error);
          localities.push(null);
        }
      }

      setLocalities(localities);

      /**MERGE INTERMEDIATE POINTS WITH LOCALITIES ASSOCIATED */
      const absLocality = [];
      for (let i = 0; i < intermediatePoints.length; i++) {
        if (
          intermediatePoints[i][0] !== null &&
          intermediatePoints[i][1] !== null &&
          localities[i] != null
        ) {
          const mergedLocation = {
            point: intermediatePoints[i],
            timeToArrive: intermediatePoints[i].timeToArrive,
            localityName: localities[i].locality,
            localityProvince: localities[i].province,
            localityPoint: {
              lat: localities[i].location.lat(),
              lng: localities[i].location.lng(),
            },
            country: localities[i].country,
          };

          absLocality.push(mergedLocation);
        }
      }

      routeMetadata = {
        ...routeMetadata,
        polyline: polyline_encoded,
        origin: localitiesToStore[0]?.localityName
          ? localitiesToStore[0]?.localityName
          : directionsServiceOptions.origin,
        originCountry: originCountry,
        destinationCountry: destinationCountry,
        destination: localitiesToStore[localitiesToStore.length - 1]
          ?.localityName
          ? localitiesToStore[localitiesToStore.length - 1]?.localityName
          : directionsServiceOptions.destination,
        localities: localitiesToStore,
        avoidFerries: directionsServiceOptions.avoidFerries,
        avoidTolls: directionsServiceOptions.avoidTolls,
        avoidHighways: directionsServiceOptions.avoidHighways,
        distanceThreshold: distanceThreshold,
        averageSpeed: speedValues[directionsServiceOptions.travelMode],
        travelMode: directionsServiceOptions.travelMode,
        totalDistance: totalDistance,
        waypoints:
          directionsServiceOptions.waypoints.length > 0
            ? directionsServiceOptions.waypoints.map(waypoint => waypoint.value)
            : [],
      };

      if (intermediatePoints.length > absLocality.length) {
        const diff =
          ((intermediatePoints.length - absLocality.length) * 100) /
          intermediatePoints.length;
        if (diff > 10) {
          toast({
            title: t('map.errors.partialLoadTitle'),
            description: t('map.errors.partialLoadDesc', {
              count: intermediatePoints.length - absLocality.length,
            }),
            status: 'warning',
            duration: 2000,
            isClosable: true,
          });
        }
        routeMetadata.status = 'INCOMPLETE';
      }
      if (!routeMetadata.status) {
        routeMetadata.status = 'OK';
      }
      const newStoredRoute = await RouteApi.createRoute(routeMetadata);
      if (newStoredRoute) {
        console.log(newStoredRoute._id);
        setApiData(newStoredRoute._id);
      }
      setAbsoluteLocalities(absLocality);
    } else if (status === 'NOT_FOUND' || status === 'ZERO_RESULTS') {
      setErrorMessage();
      toast({
        title: t('map.errors.notFoundTitle'),
        description: t('map.errors.notFoundDesc'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setAlreadyCalculated(false);
      setLocalitiesAndWeather(null);
      setIsFormVisible(true);
      setIsLoading(false);
    } else if (status !== 'OK') {
      setIsLoading(false);
      toast({
        title: t('map.errors.unexpectedTitle'),
        description: t('map.errors.unexpectedDesc'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFormSubmit = () => {
    if (
      directionsServiceOptions.destination !== '' &&
      directionsServiceOptions.origin !== ''
    ) {
      setResponse(null);
      if (actualPolyline) actualPolyline.setMap(null);
      setIsLoading(true);
      setIndexToRender(null);
      setLocalitiesAndWeather(null);
      setAlreadyCalculated(true);
      setIsFormVisible(false);
      const directionsService = new window.google.maps.DirectionsService();

      const waypoints = [];

      // Iterate through the waypoints and construct them as objects
      for (const key in directionsServiceOptions.waypoints) {
        if (directionsServiceOptions.waypoints.hasOwnProperty(key)) {
          const value = directionsServiceOptions.waypoints[key].value;
          waypoints.push({
            location: value, // Set the location property to the waypoint's value
            stopover: true, // You can specify other properties like stopover as needed
          });
        }
      }

      directionsService.route(
        {
          origin: directionsServiceOptions.origin,
          destination: directionsServiceOptions.destination,
          travelMode: directionsServiceOptions.travelMode,
          waypoints: waypoints.length > 0 ? waypoints : null,
          avoidFerries: directionsServiceOptions.avoidFerries,
          avoidHighways: directionsServiceOptions.avoidHighways,
          avoidTolls: directionsServiceOptions.avoidTolls,
          provideRouteAlternatives: false,
        },
        directionsCallback
      );
    }
  };

  const onTravelModeChange = e => {
    setAlreadyCalculated(false);
    setDirectionsServiceOptions({
      ...directionsServiceOptions,
      travelMode: e,
    });
  };

  /* USER ACTIONS */

  const handleOnDrag = () => {
    if (!isLargerThanMobile) {
      setIsContextualMenuVisible(false);
    }
  };

  const handleMouseDown = e => {
    /*  setLongPressTimer(
      setTimeout(() => {
        setIsLongPress(e);
      }, 500)
    ); */
    // Adjust the timeout duration as needed
  };

  const handleMouseUp = () => {
    clearTimeout(longPressTimer);
    if (isLongPress) {
      // Long press action has already been triggered, reset the flag
      setIsLongPress(false);
    } else {
      // Short press action (e.g., click) - handle it here
      console.log('Short press detected!');
    }
  };

  const handleUserMarkerOnClick = e => {
    setIsContextualMenuVisible(true);
  };

  /*MAP OPTIONS*/
  const handleZoom = 2;

  const handleOnLoad = map => setMap(map);

  const handleLocationSelect = (location, type) => {
    if (!location || !map) return;
    const latLng = { lat: location.lat(), lng: location.lng() };
    if (type === 'origin') setOriginLocation(latLng);
    else setDestinationLocation(latLng);
    map.panTo(latLng);
    setTimeout(() => map.setZoom(10), 600);
  };

  const mapContainerStyle = {
    width: indexToRender !== null && isLargerThanMobile ? '70%' : '100%',
    height: '100%',
    minHeight:
      indexToRender !== null && !isLargerThanMobile ? '50dvh' : '100dvh',
  };

  useEffect(() => {
    if (routeData) {
      setIsFormVisible(false);
      console.log(routeData.polyline);
      if (routeData.polyline)
        setDecodedPolyline(polyline.decode(routeData?.polyline));
    }
  }, [routeData]);

  return isLoaded ? (
    <Box>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        onLoad={handleOnLoad}
        zoom={handleZoom}
        options={mapOptions}
        onCenterChanged={() => {}}
        onBoundsChanged={handleOnDrag}
        onClick={e => {}}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onDrag={handleOnDrag}
      >
        {localitiesAndWeather?.map((locality, index) => {
          if (!locality) return null;
          const { lat, lng } = getLocalityLatLng(locality);
          // Pick the forecast for the estimated passing time so the map icon
          // reflects the weather the traveler will actually find there.
          const passingHour = getForecastAtPassingTime(
            locality.weatherForecast,
            locality.passingTime
          );
          const iconCode = resolveWeatherIconCode(
            passingHour?.weatherCode,
            passingHour?.period
          );
          const timeLabel = formatPassingTime(locality.passingTime);
          return (
            <Marker
              key={index}
              position={{ lat, lng }}
              icon={{
                url: require(
                  `../../../../assets/weathericons/${iconCode}_g.png`
                ),
                scaledSize: new window.google.maps.Size(40, 40),
                labelOrigin: new window.google.maps.Point(20, 50),
              }}
              label={
                timeLabel
                  ? {
                      text: timeLabel,
                      className: 'marker-time-label',
                      color: '#1a202c',
                      fontSize: '11px',
                      fontWeight: '700',
                    }
                  : undefined
              }
              onClick={() => {
                setIsFormVisible(false);
                setIndexToRender(index);
              }}
              zIndex={6}
            />
          );
        })}
        {indexToRender !== null &&
          localitiesAndWeather[indexToRender] !== null && (
            <MapOverlay position={getLocalityLatLng(localitiesAndWeather[indexToRender])} />
          )}
        {originLocation && (
          <Marker
            position={originLocation}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#38A169',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
            zIndex={7}
          />
        )}
        {destinationLocation && (
          <Marker
            position={destinationLocation}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#E53E3E',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
            zIndex={7}
          />
        )}
      </GoogleMap>
      {isFormVisible && (
        <RouteForm
          TravelMode={TravelMode}
          onTravelModeChange={onTravelModeChange}
          handleSubmit={handleFormSubmit}
          onLocationSelect={handleLocationSelect}
          alreadyCalculated={alreadyCalculated}
          isVisible={isFormVisible}
          errorMessage={errorMessage}
          handleDeleteWaypointFromDirectionsService={handleDeleteWaypoint}
          setDirectionsServiceOptions={setDirectionsServiceOptions}
          directionsServiceOptions={directionsServiceOptions}
          setAlreadyCalculated={setAlreadyCalculated}
          departureTime={departureTime}
          setDepartureTime={setDepartureTime}
          hasWarningBanner={hasWarningBanner}
        />
      )}

      <Box
        position={'absolute'}
        top={hasWarningBanner ? '50px' : 2}
        left={2}
        p={2}
        bgColor={'#c4c4c440'}
        zIndex={1}
      >
        <VStack>
          <CustomIconButton
            mapStyle={mapStyle}
            icon={<SearchIcon />}
            onClick={() => {
              setIsFormVisible(!isFormVisible);
              setIndexToRender(null);
            }}
          />
          <CustomIconButton
            mapStyle={mapStyle}
            icon={<MoonIcon />}
            onClick={() => {
              setMapStyle(
                mapStyle === MAP_STYLE.LIGHT ? MAP_STYLE.DARK : MAP_STYLE.LIGHT
              );
            }}
          />
          {localitiesAndWeather !== null && (apiData || routeData?._id) && (
            <CustomIconButton
              mapStyle={mapStyle}
              icon={<FaChartArea />}
              onClick={() => {
                if (onOpenProfile) {
                  onOpenProfile(mapStyle === MAP_STYLE.DARK);
                }
              }}
            />
          )}
          {localitiesAndWeather !== null && (
            <CustomIconButton
              mapStyle={mapStyle}
              icon={navigator.share ? <FaShareAlt /> : <FaLink />}
              onClick={() => {
                if (navigator.share) {
                  navigator
                    .share({
                      title: 'WebPage Title',
                      url: routeData
                        ? window.location.href
                        : `https://map.routeforecast.app/map/route/${apiData}`,
                    })
                    .then(() => console.log('Successful share'))
                    .catch(error => console.log('Error sharing', error));
                } else {
                  navigator.clipboard.writeText(
                    routeData
                      ? window.location.href
                      : `https://map.routeforecast.app/map/route/${apiData}`
                  );
                  toast({
                    title: t('map.errors.linkCopied'),
                    duration: 2000,
                    isClosable: true,
                    status: 'info',
                    icon: <FaLink />,
                  });
                }
              }}
            />
          )}
        </VStack>
      </Box>
    </Box>
  ) : (
    <LoadingMap />
  );
}

function CustomIconButton({ onClick, icon, mapStyle }) {
  const [color, setColor] = useState(
    mapStyle === MAP_STYLE.LIGHT ? '#1A202C' : '#EDF2F7'
  );
  return (
    <IconButton
      onMouseOver={() => {
        setColor(mapStyle === MAP_STYLE.LIGHT ? '#1A202C' : '#EDF2F7');
      }}
      onMouseLeave={() => {
        setColor(mapStyle === MAP_STYLE.LIGHT ? '#EDF2F7' : '#1A202C');
      }}
      icon={icon}
      onClick={onClick}
      backgroundColor={mapStyle === MAP_STYLE.LIGHT ? '#EDF2F7' : '#1A202C'}
      color={mapStyle === MAP_STYLE.LIGHT ? '#1A202C' : '#EDF2F7'}
    />
  );
}

function transformToLatLngArray(decodedPolyline) {
  if (!Array.isArray(decodedPolyline)) {
    throw new Error('Invalid input format. Expecting an array.');
  }

  return decodedPolyline.map(tuple => {
    if (Array.isArray(tuple) && tuple.length === 2) {
      const [lat, lng] = tuple;
      return { lat, lng };
    } else {
      throw new Error(
        'Invalid tuple format. Expecting an array with two values.'
      );
    }
  });
}

function MapOverlay({ position }) {
  return (
    <OverlayView
      position={{ lat: position.lat, lng: position.lng }}
      mapPaneName={OverlayView.OVERLAY_LAYER}
      getPixelPositionOffset={(width, height) => ({
        x: -width / 2,
        y: -height / 2,
      })}
      zIndex={4}
    >
      <div
        style={{
          width: '50px',
          height: '50px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transform: 'translate(-50%, -90%)', // Center the overlay on the marker
          background: '#38A169',
          opacity: 0.4,
          /* border: '3px solid', */
          borderColor: '#38A169',
          borderRadius: 50,
          zIndex: 10,
        }}
      ></div>
    </OverlayView>
  );
}

function calculateDistanceThreshold(distance) {
  const distanceInMeters = distance * 1000; // Convert km to meters

  // Calculate the distanceThreshold using an implicit formula
  const factor =
    distanceInMeters <= 100000
      ? 5
      : distanceInMeters <= 200000
        ? 7
        : distanceInMeters <= 300000
          ? 10
          : Math.ceil(distanceInMeters / 30000);
  const distanceThreshold = distanceInMeters / factor;

  return distanceThreshold;
}

function extractLocalityFromGeocodingResponse(geocodingResponse) {
  const localityComponent = geocodingResponse.address_components.find(
    component => component.types.includes('locality')
  );

  return localityComponent ? localityComponent.long_name : null;
}

function extractProvinceFromGeocodingResponse(geocodingResponse) {
  const localityComponent = geocodingResponse.address_components.find(
    component => component.types.includes('administrative_area_level_2')
  );

  return localityComponent ? localityComponent.long_name : null;
}

function extractLocalityBypassFromGeocodingResponse(geocodingResponse) {
  const localityComponent = geocodingResponse.address_components.find(
    component => component.types.includes('administrative_area_level_3')
  );

  return localityComponent ? localityComponent.long_name : null;
}

function extractFromGeopoint(geocodingResponse, area) {
  const localityComponent = geocodingResponse.address_components.find(
    component => component.types.includes(area)
  );

  return localityComponent ? localityComponent.long_name : null;
}

export default React.memo(MapFrame);
